import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Header, Footer } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  GitCompare, ArrowLeft, Star, MapPin, DollarSign, Clock, Users, 
  Home, BadgeCheck, Crown, Phone, MessageCircle, Check, X, Building2,
  Brain, Heart, Sparkles, Loader2
} from 'lucide-react';
import { useCompare } from '../contexts/CompareContext';
import { InfoBadge } from '../components/TreatmentInfo';
import { API } from '../utils/api';


// Helper function to parse modality (handles both array and concatenated strings)
function parseModality(modality) {
  if (!modality) return [];
  if (Array.isArray(modality)) return modality;
  
  var modalityStr = String(modality).toLowerCase();
  var result = [];
  
  if (modalityStr.includes('residencial')) result.push('Residencial');
  if (modalityStr.includes('ambulatorio')) result.push('Ambulatorio');
  if (modalityStr.includes('online') || modalityStr.includes('línea')) result.push('Online');
  if (modalityStr.includes('domicilio')) result.push('A domicilio');
  if (modalityStr.includes('semi')) result.push('Semi-residencial');
  
  if (result.length === 0 && modality) result.push(modality);
  
  return result;
}

// Labels
const modalityLabels = {
  residencial: 'Residencial',
  ambulatorio: 'Ambulatorio',
  online: 'Online',
  mixto: 'Mixto',
  presencial: 'Presencial',
  hibrido: 'Híbrido'
};

const genderLabels = {
  mixed: 'Mixto',
  male: 'Solo hombres',
  female: 'Solo mujeres'
};

function ComparePage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { clearCompare } = useCompare();
  const [centers, setCenters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const ids = searchParams.get('ids')?.split(',').filter(Boolean) || [];

  useEffect(() => {
    if (ids.length < 2) {
      setError('Selecciona al menos 2 centros para comparar');
      setLoading(false);
      return;
    }

    async function fetchCenters() {
      try {
        const response = await axios.get(`${API}/centers/compare`, {
          params: { ids: ids.join(',') }
        });
        setCenters(response.data.centers || []);
      } catch (err) {
        setError('Error al cargar los centros');
        console.error(err);
      } finally {
        setLoading(false);
      }
    }

    fetchCenters();
  }, [searchParams]);

  const formatPrice = (min, max) => {
    if (!min && !max) return 'Consultar';
    const format = (n) => n ? `$${(n/1000).toFixed(0)}K` : '';
    if (min && max) return `${format(min)} - ${format(max)}`;
    if (min) return `Desde ${format(min)}`;
    return `Hasta ${format(max)}`;
  };

  const formatDuration = (min, max) => {
    if (!min && !max) return 'Variable';
    if (min && max) return `${min} - ${max} meses`;
    if (min) return `Desde ${min} meses`;
    return `Hasta ${max} meses`;
  };

  const formatWhatsAppLink = (phone) => {
    if (!phone) return null;
    const cleaned = phone.replace(/\D/g, '');
    return `https://wa.me/${cleaned.startsWith('56') ? cleaned : '56' + cleaned}`;
  };

  // Get all unique services across all centers
  const allServices = [...new Set(centers.flatMap(c => c.services || []))];

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950">
        <Header />
        <div className="flex items-center justify-center py-32">
          <Loader2 className="w-8 h-8 text-emerald-400 animate-spin" />
        </div>
        <Footer />
      </div>
    );
  }

  if (error || centers.length < 2) {
    return (
      <div className="min-h-screen bg-slate-950">
        <Header />
        <div className="max-w-4xl mx-auto px-4 py-16 text-center">
          <GitCompare className="w-16 h-16 text-slate-600 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-slate-50 mb-4">
            {error || 'Selecciona centros para comparar'}
          </h1>
          <p className="text-slate-400 mb-8">
            Necesitas seleccionar al menos 2 centros para ver una comparación.
          </p>
          <Link to="/centros-de-rehabilitacion-drogas-chile">
            <Button className="bg-emerald-500 hover:bg-emerald-600">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Ir a Centros
            </Button>
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950">
      
        <title>Comparar Centros de Rehabilitación | SinAdicciones.org</title>
        <meta name="description" content="Compara centros de rehabilitación de adicciones en Chile. Precios, modalidades, servicios y calificaciones lado a lado." />
        <link rel="canonical" href="https://sinadicciones.org/comparar" />
        <meta property="og:image" content="https://sinadicciones.org/og-image.png" />
        <meta property="og:image:width" content="1280" />
        <meta property="og:image:height" content="896" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://sinadicciones.org/comparar" />
        <meta property="og:site_name" content="SinAdicciones.org" />
        <meta property="og:locale" content="es_CL" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="https://sinadicciones.org/og-image.png" />

        <meta name="robots" content="noindex, follow" />
        <meta property="og:title" content="Comparar Centros de Rehabilitación | SinAdicciones.org" />
        <meta property="og:description" content="Compara centros de rehabilitación de adicciones en Chile lado a lado." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://sinadicciones.org/comparar" />
      

      <Header />

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <Link to="/centros-de-rehabilitacion-drogas-chile" className="text-emerald-400 hover:text-emerald-300 flex items-center gap-2 mb-2">
              <ArrowLeft className="w-4 h-4" />
              Volver a Centros
            </Link>
            <h1 className="text-3xl font-bold text-slate-50 flex items-center gap-3">
              <GitCompare className="w-8 h-8 text-emerald-400" />
              Comparar Centros
            </h1>
            <p className="text-slate-400 mt-1">Comparando {centers.length} centros de rehabilitación</p>
          </div>
          <Button 
            variant="outline" 
            onClick={() => { clearCompare(); navigate('/centros'); }}
            className="border-slate-700 text-slate-300"
          >
            Nueva Comparación
          </Button>
        </div>

        {/* Comparison Table */}
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            {/* Header with center names */}
            <thead>
              <tr>
                <th className="w-48 p-4 text-left text-slate-500 font-medium bg-slate-900/50 rounded-tl-lg">
                  Característica
                </th>
                {centers.map((center, idx) => (
                  <th key={center.center_id} className={`p-4 bg-slate-900/50 min-w-[220px] ${idx === centers.length - 1 ? 'rounded-tr-lg' : ''}`}>
                    <div className="text-center">
                      {/* Center Image */}
                      <div className="w-full h-32 rounded-lg overflow-hidden mb-3 bg-slate-800">
                        {center.logo || center.gallery?.[0] ? (
                          <img 
                            src={center.logo || center.gallery[0]} 
                            alt={center.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Building2 className="w-12 h-12 text-slate-600" />
                          </div>
                        )}
                      </div>
                      {/* Badges */}
                      <div className="flex items-center justify-center gap-1 mb-2">
                        {center.is_premium && (
                          <span className="bg-amber-500/20 text-amber-400 text-xs px-2 py-0.5 rounded-full flex items-center gap-1">
                            <Crown className="w-3 h-3" /> Premium
                          </span>
                        )}
                        {center.is_verified && (
                          <span className="bg-emerald-500/20 text-emerald-400 text-xs px-2 py-0.5 rounded-full flex items-center gap-1">
                            <BadgeCheck className="w-3 h-3" /> Verificado
                          </span>
                        )}
                      </div>
                      {/* Name */}
                      <h3 className="text-slate-50 font-semibold text-sm line-clamp-2">
                        {center.name}
                      </h3>
                      {/* City */}
                      <p className="text-slate-400 text-xs flex items-center justify-center gap-1 mt-1">
                        <MapPin className="w-3 h-3" /> {center.city || 'Chile'}
                      </p>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {/* Rating */}
              <tr className="border-t border-slate-800">
                <td className="p-4 text-slate-400 font-medium bg-slate-900/30">
                  <div className="flex items-center gap-2">
                    <Star className="w-4 h-4 text-amber-400" />
                    Calificación
                  </div>
                </td>
                {centers.map(center => (
                  <td key={center.center_id} className="p-4 text-center">
                    {center.average_rating ? (
                      <div className="flex items-center justify-center gap-2">
                        <span className="text-amber-400 font-bold text-lg">{center.average_rating.toFixed(1)}</span>
                        <Star className="w-5 h-5 text-amber-400 fill-amber-400" />
                        <span className="text-slate-500 text-sm">({center.total_reviews || 0})</span>
                      </div>
                    ) : (
                      <span className="text-slate-500">Sin reseñas</span>
                    )}
                  </td>
                ))}
              </tr>

              {/* Price */}
              <tr className="border-t border-slate-800">
                <td className="p-4 text-slate-400 font-medium bg-slate-900/30">
                  <div className="flex items-center gap-2">
                    <DollarSign className="w-4 h-4 text-emerald-400" />
                    Precio/mes
                  </div>
                </td>
                {centers.map(center => (
                  <td key={center.center_id} className="p-4 text-center">
                    <span className="text-emerald-400 font-semibold">
                      {formatPrice(center.price_min, center.price_max)}
                    </span>
                  </td>
                ))}
              </tr>

              {/* Modality */}
              <tr className="border-t border-slate-800">
                <td className="p-4 text-slate-400 font-medium bg-slate-900/30">
                  <div className="flex items-center gap-2">
                    <Home className="w-4 h-4 text-blue-400" />
                    Modalidad
                  </div>
                </td>
                {centers.map(center => (
                  <td key={center.center_id} className="p-4">
                    <div className="flex flex-wrap justify-center gap-1">
                      {center.modality && parseModality(center.modality).length > 0 ? (
                        parseModality(center.modality).map((m, i) => (
                          <InfoBadge key={i} tipo="modalidad" valor={m} />
                        ))
                      ) : (
                        <span className="text-slate-500">No especificado</span>
                      )}
                    </div>
                  </td>
                ))}
              </tr>

              {/* Therapy Approach */}
              <tr className="border-t border-slate-800">
                <td className="p-4 text-slate-400 font-medium bg-slate-900/30">
                  <div className="flex items-center gap-2">
                    <Brain className="w-4 h-4 text-purple-400" />
                    Tipo de Tratamiento
                  </div>
                </td>
                {centers.map(center => (
                  <td key={center.center_id} className="p-4">
                    <div className="flex flex-wrap justify-center gap-1">
                      {center.therapy_approaches?.length > 0 ? (
                        center.therapy_approaches.map((t, i) => (
                          <InfoBadge key={i} tipo="enfoque" valor={t} />
                        ))
                      ) : (
                        <span className="text-slate-500">No especificado</span>
                      )}
                    </div>
                  </td>
                ))}
              </tr>

              {/* Duration */}
              <tr className="border-t border-slate-800">
                <td className="p-4 text-slate-400 font-medium bg-slate-900/30">
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-cyan-400" />
                    Duración
                  </div>
                </td>
                {centers.map(center => (
                  <td key={center.center_id} className="p-4 text-center">
                    <span className="text-slate-300">
                      {formatDuration(center.treatment_duration_min, center.treatment_duration_max)}
                    </span>
                  </td>
                ))}
              </tr>

              {/* Gender */}
              <tr className="border-t border-slate-800">
                <td className="p-4 text-slate-400 font-medium bg-slate-900/30">
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-pink-400" />
                    Género
                  </div>
                </td>
                {centers.map(center => (
                  <td key={center.center_id} className="p-4 text-center">
                    <span className="text-slate-300">
                      {genderLabels[center.gender] || 'Mixto'}
                    </span>
                  </td>
                ))}
              </tr>

              {/* Specializations */}
              <tr className="border-t border-slate-800">
                <td className="p-4 text-slate-400 font-medium bg-slate-900/30">
                  <div className="flex items-center gap-2">
                    <Heart className="w-4 h-4 text-red-400" />
                    Adicciones que tratan
                  </div>
                </td>
                {centers.map(center => (
                  <td key={center.center_id} className="p-4">
                    <div className="flex flex-wrap justify-center gap-1">
                      {center.specializations?.length > 0 ? (
                        center.specializations.map((s, i) => (
                          <span key={i} className="text-xs px-2 py-0.5 bg-slate-700 text-slate-300 rounded">
                            {s}
                          </span>
                        ))
                      ) : (
                        <span className="text-slate-500">General</span>
                      )}
                    </div>
                  </td>
                ))}
              </tr>

              {/* Services comparison */}
              {allServices.length > 0 && (
                <>
                  <tr className="border-t-2 border-slate-700">
                    <td colSpan={centers.length + 1} className="p-4 bg-slate-800/50">
                      <h3 className="text-slate-50 font-semibold flex items-center gap-2">
                        <Sparkles className="w-4 h-4 text-emerald-400" />
                        Servicios Incluidos
                      </h3>
                    </td>
                  </tr>
                  {allServices.slice(0, 10).map((service, idx) => (
                    <tr key={service} className="border-t border-slate-800">
                      <td className="p-3 text-slate-400 text-sm bg-slate-900/30">
                        {service}
                      </td>
                      {centers.map(center => {
                        const hasService = center.services?.includes(service);
                        return (
                          <td key={center.center_id} className="p-3 text-center">
                            {hasService ? (
                              <Check className="w-5 h-5 text-emerald-400 mx-auto" />
                            ) : (
                              <X className="w-5 h-5 text-slate-600 mx-auto" />
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </>
              )}

              {/* Actions */}
              <tr className="border-t-2 border-slate-700">
                <td className="p-4 bg-slate-900/30"></td>
                {centers.map(center => (
                  <td key={center.center_id} className="p-4">
                    <div className="flex flex-col gap-2">
                      <Link to={`/centros/${center.slug || center.center_id}`}>
                        <Button className="w-full bg-emerald-500 hover:bg-emerald-600">
                          Ver Perfil Completo
                        </Button>
                      </Link>
                      {(center.phone || center.whatsapp) && (
                        <a 
                          href={formatWhatsAppLink(center.whatsapp || center.phone)}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <Button variant="outline" className="w-full border-green-500 text-green-400 hover:bg-green-500/10">
                            <MessageCircle className="w-4 h-4 mr-2" />
                            WhatsApp
                          </Button>
                        </a>
                      )}
                    </div>
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>

        {/* Mobile hint */}
        <p className="text-center text-slate-500 text-sm mt-6 md:hidden">
          Desliza horizontalmente para ver toda la comparación
        </p>
      </main>

      <Footer />
    </div>
  );
}

export default ComparePage;
